import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  Menu as MenuIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Support as SupportIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import ContactSupportDialog from './ContactSupportDialog';
import SiteFooter from './shared/SiteFooter';
import { INK, LINE } from '../constants/brand';

const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon />, path: '/app' },
    { text: 'Saved Searches', icon: <SaveIcon />, path: '/app/saved-searches' },
    { text: 'Account', icon: <PersonIcon />, path: '/app/account' },
  ];

  const toggleDrawer = (open) => (event) => {
    if (event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setDrawerOpen(false);
  };

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)}>
      <Box sx={{ p: 2 }}>
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 1,
            cursor: 'pointer'
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigate('/');
          }}
        >
          <img 
            src="/shield_logo.png" 
            alt="Diligence Desk Shield Logo" 
            style={{ 
              height: '24px', 
              width: 'auto' 
            }}
          />
          <Typography variant="h6" color="primary">
            Diligence Desk
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {user?.email}
        </Typography>
      </Box>
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton 
              onClick={() => handleNavigation(item.path)}
              sx={{
                minHeight: 48, // Touch-friendly height for list items
                py: 1.5        // Better vertical padding
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
      <List>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={() => setContactSupportOpen(true)}
            sx={{
              minHeight: 48, // Touch-friendly height for list items
              py: 1.5        // Better vertical padding
            }}
          >
            <ListItemIcon>
              <SupportIcon />
            </ListItemIcon>
            <ListItemText primary="Contact Support" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={handleLogout}
            sx={{
              minHeight: 48, // Touch-friendly height for list items
              py: 1.5        // Better vertical padding
            }}
          >
            <ListItemIcon>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'rgba(255,255,255,0.94)',
          color: INK,
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${LINE}`,
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, gap: 1 }}>
          <Button
            aria-label="open drawer"
            onClick={toggleDrawer(true)}
            sx={{
              display: { md: 'none' },
              color: INK,
              minWidth: 40,
              minHeight: 44,
              mr: 0.5,
            }}
          >
            <MenuIcon />
          </Button>
          <Box
            onClick={() => navigate('/app')}
            sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', mr: 1 }}
          >
            <img src="/shield_logo.png" alt="" style={{ height: 28, width: 'auto' }} />
            <Typography sx={{ fontWeight: 750, color: 'primary.main', fontSize: '1.05rem', display: { xs: 'none', sm: 'block' } }}>
              Diligence Desk
            </Typography>
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5, flexGrow: 1 }}>
            {menuItems.map((item) => {
              const active = item.path === '/app' ? pathname === '/app' : pathname.startsWith(item.path);
              return (
                <Button
                  key={item.text}
                  onClick={() => handleNavigation(item.path)}
                  sx={{
                    color: active ? 'primary.main' : INK,
                    fontWeight: active ? 700 : 600,
                    textTransform: 'none',
                  }}
                >
                  {item.text}
                </Button>
              );
            })}
          </Box>

          <Box sx={{ flexGrow: { xs: 1, md: 0 } }} />

          <Button
            onClick={() => setContactSupportOpen(true)}
            sx={{ display: { xs: 'none', md: 'inline-flex' }, color: INK, textTransform: 'none', fontWeight: 600 }}
          >
            Support
          </Button>
          <Button
            onClick={handleLogout}
            sx={{ color: 'primary.main', textTransform: 'none', fontWeight: 650, minHeight: 44 }}
          >
            Sign out
          </Button>
        </Toolbar>
      </AppBar>
      
      <Drawer anchor="left" open={drawerOpen} onClose={toggleDrawer(false)}>
        {drawer}
      </Drawer>
      
      <Box
        component="main"
        sx={{
          flex: 1,
          mt: { xs: 7, sm: 8 },
          width: '100%',
          maxWidth: '100vw',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.default'
        }}
      >
        <Box sx={{ 
          flex: 1,
          width: '100%',
          maxWidth: '100%',
          mx: 'auto',
          px: 0,
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          backgroundColor: 'background.default'
        }}>
          <Outlet />
        </Box>
      </Box>
      
      <SiteFooter />
      
      {/* Contact Support Dialog */}
      <ContactSupportDialog 
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
      />
    </Box>
  );
};

export default Layout;
